import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Stack,
  Alert,
  styled,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AnimatedSection from '../components/AnimatedSection';

// Mover los componentes styled fuera del componente para evitar re-renders
const SectionContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(8),
  paddingBottom: theme.spacing(8),
  backgroundColor: 'transparent',
}));

const Title = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '2rem',
  marginBottom: theme.spacing(1),
  textAlign: 'center',
  color: '#000000',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.5rem',
  },
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  marginBottom: theme.spacing(4),
  textAlign: 'center',
  color: '#666666',
}));

const ContactSection = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [num1, setNum1] = useState(0);
  const [num2, setNum2] = useState(0);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef(null);
  const hasAttemptedSubmit = useRef(false);

  // Generar números aleatorios para el captcha
  useEffect(() => {
    if (showCaptcha) {
      setNum1(Math.floor(Math.random() * 10) + 1);
      setNum2(Math.floor(Math.random() * 10) + 1);
    }
  }, [showCaptcha]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    // Validar email
    if (!email) {
      setMessage({ type: 'error', text: t('contact.error') });
      return;
    }

    if (!validateEmail(email)) {
      setMessage({ type: 'error', text: t('contact.error') });
      return;
    }

    // Si es el primer intento, mostrar captcha
    if (!hasAttemptedSubmit.current) {
      hasAttemptedSubmit.current = true;
      setShowCaptcha(true);
      setMessage({ type: 'error', text: t('contact.pleaseCompleteCaptcha') || 'Por favor completa la verificación' });
      return;
    }

    // Validar captcha si está visible
    if (showCaptcha) {
      const correctAnswer = num1 + num2;
      if (parseInt(captchaAnswer) !== correctAnswer) {
        setMessage({ type: 'error', text: t('contact.captchaError') });
        // Regenerar captcha
        setNum1(Math.floor(Math.random() * 10) + 1);
        setNum2(Math.floor(Math.random() * 10) + 1);
        setCaptchaAnswer('');
        return;
      }
    }

    // Enviar email usando mailto (no requiere configuración)
    setIsSubmitting(true);
    try {
      const subject = encodeURIComponent('Contacto desde web');
      const body = encodeURIComponent(`Nuevo contacto desde el formulario web.\n\nEmail del contacto: ${email}`);
      const mailtoLink = `mailto:contact@peoplefortech.com?subject=${subject}&body=${body}`;
      
      window.location.href = mailtoLink;
      
      setMessage({ type: 'success', text: t('contact.success') });
      setEmail('');
      setCaptchaAnswer('');
      setShowCaptcha(false);
      hasAttemptedSubmit.current = false;
    } catch (error) {
      console.error('Error al enviar email:', error);
      setMessage({ type: 'error', text: t('contact.error') });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatedSection direction="bottom" delay={0.3}>
      <SectionContainer component="section">
        <Stack spacing={3} alignItems="center">
          <Box>
            <Title variant="h3">{t('contact.title')}</Title>
            <Subtitle variant="body1">{t('contact.subtitle')}</Subtitle>
          </Box>

          <Box
            ref={formRef}
            component="form"
            onSubmit={handleSubmit}
            sx={{
              maxWidth: '500px',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
          <TextField
            fullWidth
            label={t('contact.email')}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('contact.emailPlaceholder')}
            required
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                },
                '&:hover fieldset': {
                  borderColor: 'black',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'black',
                },
              },
            }}
          />

          {showCaptcha && num1 > 0 && num2 > 0 && (
            <TextField
              fullWidth
              label={(() => {
                const labelTemplate = t('contact.captchaLabel');
                return labelTemplate
                  .replace('{num1}', num1)
                  .replace('{num2}', num2);
              })()}
              type="number"
              value={captchaAnswer}
              onChange={(e) => setCaptchaAnswer(e.target.value)}
              placeholder={t('contact.captchaPlaceholder')}
              required
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'black',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'black',
                  },
                },
              }}
            />
          )}

          {message.text && (
            <Alert severity={message.type === 'success' ? 'success' : 'error'}>
              {message.text}
            </Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={isSubmitting}
            sx={{
              py: 1.5,
              backgroundColor: '#000000',
              color: '#ffffff',
              fontWeight: 600,
              fontSize: '1rem',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#333333',
              },
              '&:disabled': {
                backgroundColor: '#666666',
              },
            }}
          >
            {isSubmitting ? (t('contact.sending') || 'Enviando...') : t('contact.submit')}
          </Button>
        </Box>
      </Stack>
    </SectionContainer>
    </AnimatedSection>
  );
};

export default ContactSection;

